import jsbridge from '@xpeng/js-bridge'
import ResponseType from './models/ResponseTypes'
import MessageBox from '../components/message-box'
import _ from 'lodash';
import * as Sentry from '@sentry/browser'
import { isMock, isXpAPP } from '../utils/common'

export const NETWORK_SUCCESS_CODE = 200
// 重复支付错误代码：4003
// 撤销申请或者作废：4007
export const REPEAT_PAY_CODE = 4003 //重复支付的特殊code
export const CANCEL_SETTLE_CODE = 4007 //撤销结算的特殊code

const NETWORK_TIMEOUT_SECOND = 10

export const baseService = '/voss'
export const baseUrl = `${baseService}/public/v1`

export const newBaseService = isXpAPP() || isMock() ? '/ass' : '/api/ass/logan'
export const newBaseUrl = `${newBaseService}/public/v1`

export function getNetworkTimeoutPromise(
  timeout = NETWORK_TIMEOUT_SECOND
): Promise<never> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      reject(new Error('网络超时'))
    }, timeout * 1000)
  })
}
interface OPTIONS_TYPES {
  /**
   * 是否强制使用fetch
   * @default false
   */
  useFetch?: boolean
  /**
   * @default false
   * 是否返回服务器原始响应数据，为true时格式如：
   * {
   *   "code": 200,
   *   "data": { "result": 1 },
   *   "msg": ""
   * }
   * 为 false 时，则只在`code`为200时返回`data`部分，其余`code`报错。
   */
  pureReturn?: boolean
  timeout?: number
  /**
   * 是否跳过登录态检查
   * @default false
   */
  skipLogin?: boolean
}

type requestFunc = <T = any, P = any>(
  url: string,
  params?: T,
  skipLogin?: boolean
) => Promise<ResponseType<P> | void>

/**
 * SSO 登录检查（非 APP 环境）
 * 调用 /webAuthSdk/checkLoginStatus 接口，未登录时跳转 authUrl
 */
function ssoLoginCheck(): Promise<string> {
  return fetch(`/webAuthSdk/checkLoginStatus?frontRedirect=${encodeURIComponent(window.location.href)}`, {
    method: 'GET',
    credentials: 'include',
  })
    .then((res) => res.json())
    .then((res) => {
      if (res.code === NETWORK_SUCCESS_CODE && res.data?.uid) {
        return String(res.data.uid)
      }
      // 未登录，跳转 authUrl
      if (res.data?.authUrl) {
        window.location.href = res.data.authUrl
      }
      throw new Error('SSO login required')
    })
    .catch((err) => {
      if (err.message !== 'SSO login required') {
        MessageBox.alert('登录已失效，请重新登录', {
          showCancelButton: false,
        })
      }
      throw err
    })
}

/**
 * SSO 登出
 * 调用 /webAuthSdk/checkLoginStatus 接口，已登录时跳转 logoutUrl
 */
function ssoLogout(): Promise<any> {
  return fetch(`/webAuthSdk/checkLoginStatus?frontRedirect=${encodeURIComponent(window.location.href)}`, {
    method: 'GET',
    credentials: 'include',
  })
    .then((res) => res.json())
    .then((res) => {
      if (res.code === NETWORK_SUCCESS_CODE && res.data?.logoutUrl) {
        window.location.href = res.data.logoutUrl;
        return
      }
      throw new Error(res.message);
    })
    .catch((err) => {
      MessageBox.alert('登出失败', {
        showCancelButton: false,
      })
      throw err;
    })
}

/**
 * APP 内登录检查（通过 JSBridge）
 */
function appLoginCheck(): Promise<string> {
  // Mock 模式下直接返回假的 UID，不调用 JSBridge
  if (isMock()) {
    return Promise.resolve('1101944')
  }

  return jsbridge
    .callHandler('getLoginState', {})
    .then((res) => {
      if (res.data && typeof res.data === 'string') {
        res.data = JSON.parse(res.data)
      }
      let preUid = '0'
      if (Number(res.loginState) === 1 && res.data.uid) {
        preUid = res.data.uid
      } else {
        throw new Error('login error')
      }
      return preUid
    })
    .catch((err) => {
      MessageBox.alert('登录已失效，请重新登录', {
        showCancelButton: false,
      }).then(() => {
        jsbridge
          .callHandler('goToNativePage', {
            pageType: 'login',
            data: {
              direct: window.location.href,
            },
          })
          .then(() => {})
        jsbridge.callHandler('closeWindow', {})
      })
      throw err
    })
}

export function loginCheck(): Promise<string> {
  if (isXpAPP() || isMock()) {
    return appLoginCheck()
  }
  return ssoLoginCheck()
}

export function logout(): Promise<any> {
  if (isXpAPP() || isMock()) {
    console.log('APP 暂不支持登出');
    return Promise.reject();
  }
  return ssoLogout();
}

export const requestByJsBridge: requestFunc = (url, params, skipLogin) => {
  return (skipLogin ? Promise.resolve('') : loginCheck()).then(() => {
    return jsbridge
      .callHandler('request', {
        url,
        options: {
          baseurl: `${window.location.protocol}//${window.location.host}`,
          method: 'POST',
          body: params,
        },
      })
      .then((res) => {
        return res
      })
  })
}

export const requestByFecth: requestFunc = (url, params, skipLogin) => {
  return (skipLogin ? Promise.resolve('') : loginCheck()).then((uid) => {
    return fetch(`//${window.location.host}${url}`, {
      method: 'POST',
      body: JSON.stringify(params),
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        'xp-uid': uid,
      },
    }).then((res) => res.json())
  })
}

export function originalRequest<T, P>(
  url: string,
  params?: T,
  options?: OPTIONS_TYPES
): Promise<P extends ResponseType<infer A> ? A : never> {
  let fetchFunc: requestFunc
  if (isMock() || !isXpAPP() || options?.useFetch) {
    console.log('request using fetch....')
    fetchFunc = requestByFecth
  } else {
    console.log('request using jsbridge....')
    //@ts-ignore
    fetchFunc = requestByJsBridge
  }

  return Promise.race([
    fetchFunc(url, params, options?.skipLogin),
    getNetworkTimeoutPromise(options?.timeout),
  ])
    .then((value: any) => {
      consoleRequest(url, params, value)
      if (options?.pureReturn) {
        return value
      }
      if (value.code === NETWORK_SUCCESS_CODE) {
        return value.data
      } else if (value.code === REPEAT_PAY_CODE) {
        //重复支付
        return { code: REPEAT_PAY_CODE }
      } else if (value.code === CANCEL_SETTLE_CODE) {
        //撤销结算
        return { code: CANCEL_SETTLE_CODE }
      }
      throw new Error(
        value.msg
          ? `${value.msg}`
          : `服务器返回错误码：${value.code}`
      )
    })
    .catch((error) => {
      consoleRequest(url, params, null, error)
      throw error
    })
}

export default function request<T, P>(
  url: string,
  params?: T,
  method?: string,
  options?: OPTIONS_TYPES & { useCustomUrl?: boolean }
): Promise<P extends ResponseType<infer A> ? A : never> {
  const { useCustomUrl } = options || { useCustomUrl: false }
  const fullUrl = `${useCustomUrl ? newBaseService : newBaseUrl}/${url}`
  // skipLogin 的不走 /api/ass/logan
  const finalUrl = options?.skipLogin ? fullUrl.replace('/api/ass/logan', '/ass') : fullUrl;
  return originalRequest(
    finalUrl,
    _.cloneDeepWith(params, value => {
      if (_.isString(value)) {
        return value.trim();
      }
    }),
    options
  );
}

function consoleRequest(
  url: string,
  params: any,
  response: any,
  error: any = undefined
) {
  console.log(`================ request start ============`)
  console.log(`%curl: ${url}`, 'color: red;')
  console.log('params: ', params)
  if (response) {
    console.log('response: ', response)
  }
  if (error) {
    console.log('error:', error)
  }
  Sentry.addBreadcrumb({
    type: 'http',
    message: `${url}`,
    data: {
      url,
      params,
      response: response || {},
      error: error || {},
    },
  })
  console.log(`================ request end ============`)
}
